import type { ReactElement } from "react"
import { useEffect, useRef } from "react"
import { zIndex } from "@onestore/hel/dist/settings"
import RadioGroup, {
  RadioGroupItem,
  RadioGroupLabel,
} from "@ionos-web-design-system/react/radio-group"
import Text from "@ionos-web-design-system/react/text"

type DropdownItem = {
  label: string
  onClick: () => void
  isActive: boolean
}

type ProductSelectModalProps = {
  items: DropdownItem[]
  onClose: () => void
}

export default function ProductSelectModal({
  items,
  onClose,
}: ProductSelectModalProps): ReactElement<ProductSelectModalProps> {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeItem = items.find((item) => item.isActive)

  const handleValueChange = (value: string): void => {
    const item = items.find((item) => item.label === value)
    item?.onClick()

    onClose()
  }

  useEffect(() => {
    const handleOutsideClick = (event: Event): void => {
      if (containerRef.current?.contains(event.target as Node)) {
        return
      }

      onClose()
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("click", handleOutsideClick, true)
    document.addEventListener("keydown", handleKeyDown, true)

    return () => {
      document.removeEventListener("click", handleOutsideClick, true)
      document.removeEventListener("keydown", handleKeyDown, true)
    }
  }, [onClose])

  return (
    // bg-black/40 because UDS has no color token for overlay yet,
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/40"
      style={{ zIndex: zIndex.ModalOrg - 1 }}
    >
      <div
        ref={containerRef}
        className="w-full max-w-[480px] bg-surface-base shadow-md rounded max-h-[80vh] overflow-y-auto rounded-md px-3 py-2"
      >
        <RadioGroup
          value={activeItem?.label}
          onValueChange={handleValueChange}
          className="gap-1"
        >
          {items.map((item) => (
            <RadioGroupLabel
              key={item.label}
              htmlFor={item.label}
              className="flex items-center justify-between gap-2 py-3 border-b last:border-0 border--base cursor-pointer w-full"
              data-test-id="table-dropdown-link-item"
            >
              <Text variant="body" weight="bold">
                {item.label}
              </Text>

              <RadioGroupItem id={item.label} value={item.label} />
            </RadioGroupLabel>
          ))}
        </RadioGroup>
      </div>
    </div>
  )
}
